"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = default_1;
const PrintableGroup_1 = require("../../models/printables/PrintableGroup");
const either_1 = require("../../types/either");
/**
 * Method that updates the quantity of a given printable group in the given printable group list. The total overall
 * number of copies in the list (i.e., the sum of the 'qty' field of all printable groups) needs to remain the same.
 * So, when this method increase the number of copies for a printable group, it removes an equal number of copies
 * from empty groups. Conversely, when this method decreases the number of copies in a group, it adds an equal number
 * of copies to empty printable groups.
 *
 * If there are not enough copies to take away from empty printable groups (when increasing), or if there are
 * no empty printable groups to give copies to (when decreasing), this method will throw an error. (Note, this
 * would be an odd state, as the UI should prevent these things from happening.)
 *
 */
function default_1(project, action) {
  if (action.quantity < 1) {
    return (0, either_1.left)(new Error('New quantity must be greater than 0.'));
  }
  const printableGroupIndex = project.printableGroups.findIndex(pg => pg.id === action.printableGroupId);
  // Ensure targetGroup is included in the list of printable groups
  if (printableGroupIndex === -1) {
    return (0, either_1.left)(new Error(`Could not find printable group with id '${action.printableGroupId}'.`));
  }
  const currentPrintableGroup = project.printableGroups[printableGroupIndex];
  // If new quantity is the same as the old quantity - nothing needs to be done. Just return the project
  if (currentPrintableGroup.qty === action.quantity) {
    return (0, either_1.right)(project);
  }
  // Need to know the gap between the current and new quantities. Depending on if the new quantity is an increase or decrease,
  // this is the number of copies that will need to be added to, or taken away, from empty printable groups
  const newQuantity = action.quantity;
  const difference = Math.abs(newQuantity - currentPrintableGroup.qty);
  // Quantity of target group is increasing - need to take away an appropriate number of copies from empty printable groups
  if (currentPrintableGroup.qty < action.quantity) {
    // get the empty printable groups in the list that have a copy that can be taken
    // note: this logic assumes all empty printable groups have a quantity or 1 (or 0, but those are ignored here)
    let emptyPrintableGroups = project.printableGroups.filter(x => (0, PrintableGroup_1.isPrintableGroupEmpty)(x) && x.qty >= 1 && x.id !== action.printableGroupId);
    // there needs to be an equal (or more) number of copies to take from empty printable groups as the increase in copies of the printable group
    // being changed. If not, then there aren't enough available printable slots to increase copies to desired new quantity.
    if (emptyPrintableGroups.length < difference) {
      return (0, either_1.left)(new Error(`Could not update printable group with id '${action.printableGroupId}' to quantity ${action.quantity} because there aren't enough available prints.`));
    }
    // remove copies from the appropriate number of empty groups
    emptyPrintableGroups = emptyPrintableGroups.slice(0, difference);
    emptyPrintableGroups.forEach(x => x.qty = 0);
  } else {
    // get the empty printable groups currently attached to the project that don't have any copies
    let emptyPrintableGroups = project.printableGroups.filter(x => (0, PrintableGroup_1.isPrintableGroupEmpty)(x) && x.qty == 0 && x.id !== action.printableGroupId);
    // there needs to be enough printable groups to add copies to
    if (emptyPrintableGroups.length < difference) {
      return (0, either_1.left)(new Error(`Could not update printable group with id '${action.printableGroupId}' to quantity ${action.quantity} because there aren't enough available prints.`));
    }
    // add 1 copy to the appropriate number of empty groups
    emptyPrintableGroups = emptyPrintableGroups.slice(0, difference);
    emptyPrintableGroups.forEach(x => x.qty = 1);
  }
  project.printableGroups[printableGroupIndex].qty = action.quantity;
  return (0, either_1.right)(project);
}